import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal, ModalBody, Button, Input, Spinner, MultiSelect } from '@components/ui';
import { useUpdateUser, useRoles, useBranchOffices } from '../hooks/useUsers';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import Swal from 'sweetalert2';
import type { User } from '../types/user.types';

const editUserSchema = z.object({
  email: z.string().email('Email inválido').max(100, 'Máximo 100 caracteres'),
  idNumber: z.string().min(1, 'El IDNumber es requerido').max(20, 'Máximo 20 caracteres'),
  firstName: z.string().min(1, 'El nombre es requerido').max(100, 'Máximo 100 caracteres'),
  lastName: z.string().min(1, 'El apellido es requerido').max(100, 'Máximo 100 caracteres'),
  roles: z.array(z.string()).min(1, 'Debe seleccionar al menos un rol'),
  branchOffices: z.array(z.string()).optional(),
  password: z.string().optional(),
});

type EditUserFormData = z.infer<typeof editUserSchema>;

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

export function EditUserModal({ isOpen, onClose, user }: EditUserModalProps) {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedBranchOffices, setSelectedBranchOffices] = useState<string[]>([]);

  // Roles permitidos para editar usuarios
  const ALLOWED_ROLES = ['VENDEDOR', 'CUSTOMER B2B', 'COMPANY ADMIN', 'ADMINISTRATOR'];

  const { data: rolesData, isLoading: loadingRoles } = useRoles();
  const { data: branchOfficesData, isLoading: loadingBranchOffices } = useBranchOffices();
  const { selectedCompany } = useCompanies({ navigateOnSelect: false });
  const updateMutation = useUpdateUser();

  // Filtrar solo los roles permitidos (case-insensitive) y eliminar duplicados por nombre
  const filteredRoles = rolesData?.filter(role =>
    ALLOWED_ROLES.some(allowedRole => allowedRole.toUpperCase() === role.name.toUpperCase())
  ).reduce((unique, role) => {
    // Only add if we haven't seen this role name before (case-insensitive)
    if (!unique.some(r => r.name.toUpperCase() === role.name.toUpperCase())) {
      unique.push(role);
    }
    return unique;
  }, [] as typeof rolesData) || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
  });

  // Populate form when user changes
  useEffect(() => {
    if (user && isOpen && rolesData) {
      setValue('email', user.email);
      setValue('idNumber', user.idNumber || '');
      setValue('firstName', user.firstName);
      setValue('lastName', user.lastName);

      // Set roles - normalize to match API format (case-insensitive)
      const userRoles = user.roles && user.roles.length > 0 ? user.roles : [user.role];

      // Find matching role IDs from the available roles (case-insensitive)
      const matchedRoleIds = userRoles
        .map(userRole => {
          // Try to find a matching role in filteredRoles by name (case-insensitive)
          const matchedRole = filteredRoles.find(
            r => r.name.toUpperCase() === userRole.toUpperCase()
          );
          return matchedRole?.id;
        })
        .filter((id): id is string => id !== undefined);

      setSelectedRoles(matchedRoleIds);
      setValue('roles', matchedRoleIds);

      // Set branch offices from user object - filter to only include existing ones
      const userBranchOffices = user.branchOffices || [];
      const availableBranchOfficeIds = (branchOfficesData || []).map(bo => bo.Id);
      const validBranchOffices = userBranchOffices.filter(boId =>
        availableBranchOfficeIds.includes(boId)
      );
      setSelectedBranchOffices(validBranchOffices);
      setValue('branchOffices', validBranchOffices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isOpen, rolesData, branchOfficesData]);

  const onSubmit = async (data: EditUserFormData) => {
    if (!user) return;

    try {
      await updateMutation.mutateAsync({
        id: user.id,
        data: {
          email: data.email,
          idNumber: data.idNumber,
          firstName: data.firstName,
          lastName: data.lastName,
          roles: selectedRoles,
          branchOffices: selectedBranchOffices,
          ...(data.password ? { password: data.password } : {}),
        },
      });

      await Swal.fire({
        title: 'Usuario actualizado',
        text: 'El usuario ha sido actualizado exitosamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });

      reset();
      setSelectedRoles([]);
      setSelectedBranchOffices([]);
      onClose();
    } catch (error: any) {
      Swal.fire({
        title: 'Error',
        text: error.message || 'No se pudo actualizar el usuario',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  const handleClose = () => {
    reset();
    setSelectedRoles([]);
    setSelectedBranchOffices([]);
    onClose();
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalBody>
        <div className="px-6 py-4 max-h-[80vh] overflow-y-auto">
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
            Editar Usuario
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Información de la Empresa */}
            <div className="bg-secondary-50 dark:bg-secondary-800 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-secondary-900 dark:text-white mb-2">
                Empresa
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {selectedCompany?.name || 'No hay empresa seleccionada'}
              </p>
              <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                El usuario pertenece a esta empresa
              </p>
            </div>

            {/* Información Personal */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-secondary-900 dark:text-white">
                Información Personal
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  placeholder="Juan"
                  error={errors.firstName?.message}
                  maxLength={100}
                  {...register('firstName')}
                />
                <Input
                  label="Apellido"
                  placeholder="Pérez"
                  error={errors.lastName?.message}
                  maxLength={100}
                  {...register('lastName')}
                />
                <Input
                  label="IDNumber"
                  placeholder="8-888-8888"
                  error={errors.idNumber?.message}
                  maxLength={20}
                  {...register('idNumber')}
                />
                <Input
                  label="Email"
                  type="email"
                  placeholder="usuario@ejemplo.com"
                  disabled
                  className="bg-secondary-100 dark:bg-secondary-700 cursor-not-allowed"
                  {...register('email')}
                />
              </div>
            </div>

            {/* Roles */}
            <MultiSelect
              label="Roles *"
              options={filteredRoles.map((role, index) => ({
                id: role.id || `role-${role.name}-${index}`,
                label: role.name,
                description: role.description,
              }))}
              selected={selectedRoles}
              onChange={(values) => {
                setSelectedRoles(values);
                setValue('roles', values);
              }}
              placeholder="Selecciona los roles"
              error={errors.roles?.message}
              loading={loadingRoles}
            />

            {/* Sucursales */}
            {branchOfficesData && branchOfficesData.length > 0 ? (
              <MultiSelect
                label="Sucursales (opcional)"
                options={branchOfficesData.map((branchOffice) => ({
                  id: branchOffice.Id,
                  label: branchOffice.Name,
                  description: branchOffice.Code ? `Código: ${branchOffice.Code}` : undefined,
                }))}
                selected={selectedBranchOffices}
                onChange={(values) => {
                  setSelectedBranchOffices(values);
                  setValue('branchOffices', values);
                }}
                placeholder="Selecciona las sucursales"
                error={errors.branchOffices?.message}
                loading={loadingBranchOffices}
              />
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-secondary-900 dark:text-white">
                  Sucursales (opcional)
                </label>
                {loadingBranchOffices ? (
                  <div className="flex items-center justify-center py-8 border border-secondary-300 dark:border-secondary-600 rounded-lg">
                    <Spinner size="sm" />
                  </div>
                ) : (
                  <div className="text-sm text-secondary-500 dark:text-secondary-400 p-4 border border-secondary-300 dark:border-secondary-600 rounded-lg text-center">
                    No hay sucursales disponibles para esta empresa
                  </div>
                )}
              </div>
            )}

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={updateMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending || loadingRoles || loadingBranchOffices}
              >
                {updateMutation.isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Actualizando...
                  </>
                ) : (
                  'Actualizar Usuario'
                )}
              </Button>
            </div>
          </form>
        </div>
      </ModalBody>
    </Modal>
  );
}
